import React, { JSX } from 'react';

type EventOption = { eventId: string; name?: string; status?: string };

type RaceOption = { raceId: string; name?: string };

type RunDeviceValidationOperationProps = {
  selectedIds: string[];
  selectedValidationEventId: string;
  selectedValidationRaceId: string;
  events: EventOption[];
  races: RaceOption[];
  eventsLoading: boolean;
  racesLoading: boolean;
  submitting: boolean;
  onSelectedValidationEventIdChange: (eventId: string) => void;
  onSelectedValidationRaceIdChange: (raceId: string) => void;
};

export default function RunDeviceValidationOperation(
  props: RunDeviceValidationOperationProps,
): JSX.Element {
  const {
    selectedIds,
    selectedValidationEventId,
    selectedValidationRaceId,
    events,
    races,
    eventsLoading,
    racesLoading,
    submitting,
    onSelectedValidationEventIdChange,
    onSelectedValidationRaceIdChange,
  } = props;

  return (
    <div className="border border-gray-100 dark:border-slate-700 rounded-lg p-3">
      <h3 className="text-xs font-semibold text-gray-900 dark:text-slate-100">Run device validation</h3>
      <p className="mt-1 text-[11px] text-gray-500 dark:text-slate-400">
        Choose an event and race, then validate the selected devices against that race&apos;s configuration file.
      </p>
      <label className="block text-[11px] text-gray-700 dark:text-slate-300 mb-1 mt-3">Event</label>
      <select
        className="w-full rounded-md border border-gray-300 dark:border-slate-600 px-2 py-1 text-[11px] bg-white dark:bg-slate-900 disabled:opacity-60"
        value={selectedValidationEventId}
        disabled={eventsLoading || submitting}
        onChange={(e) => onSelectedValidationEventIdChange(e.target.value)}
      >
        <option value="">Select event</option>
        {events.map((ev) => (
          <option key={ev.eventId} value={ev.eventId}>
            {ev.name ? `${ev.name} (${ev.eventId})` : ev.eventId}
          </option>
        ))}
      </select>
      {eventsLoading ? (
        <div className="mt-1 text-[11px] text-gray-500 dark:text-slate-400">Loading events...</div>
      ) : null}

      <label className="block text-[11px] text-gray-700 dark:text-slate-300 mb-1 mt-3">Race</label>
      <select
        className="w-full rounded-md border border-gray-300 dark:border-slate-600 px-2 py-1 text-[11px] bg-white dark:bg-slate-900 disabled:opacity-60"
        value={selectedValidationRaceId}
        disabled={!selectedValidationEventId || racesLoading || submitting}
        onChange={(e) => onSelectedValidationRaceIdChange(e.target.value)}
      >
        <option value="">{selectedValidationEventId ? 'Select race' : 'Select an event first'}</option>
        {races.map((r) => (
          <option key={r.raceId} value={r.raceId}>
            {r.name ? `${r.name} (${r.raceId})` : r.raceId}
          </option>
        ))}
      </select>
      {selectedValidationEventId && racesLoading ? (
        <div className="mt-1 text-[11px] text-gray-500 dark:text-slate-400">Loading races...</div>
      ) : null}

      <div className="mt-3 border border-gray-100 dark:border-slate-700 rounded-md p-2 bg-gray-50 dark:bg-slate-800">
        <div className="text-[11px] text-gray-800 dark:text-slate-200 font-medium mb-1">
          Selected devices ({selectedIds.length})
        </div>
        <div className="max-h-28 overflow-auto">
          {selectedIds.length > 0 ? (
            selectedIds.map((id) => (
              <div key={id} className="font-mono text-[11px] break-all text-gray-800 dark:text-slate-200">
                {id}
              </div>
            ))
          ) : (
            <div className="text-[11px] text-gray-400 dark:text-slate-400">No devices selected.</div>
          )}
        </div>
      </div>
    </div>
  );
}
